import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnInit,
  QueryList,
  ViewChildren,
  ChangeDetectionStrategy
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TimedOutData } from '../../models/loginResult';
import { AuthService } from '../../services/auth.service';
import { MyConfigService } from '../../services/my-config.service';
import { SnackbarService } from '../../services/snackbar.service';
import { StateService } from '../../services/state.service';
import { ForgotPasswordComponent } from '../forgot-password/forgot-password.component';
import { EventQueueService } from '../../services/eventqueue.service';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-login-dialog',
  templateUrl: './login-dialog.component.html',
  styleUrls: ['./login-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class LoginDialogComponent implements OnInit, AfterViewInit {
  loginForm: UntypedFormGroup;
  loginSmsForm: UntypedFormGroup;
  showSmsCode: boolean = false;
  loading = false;
  returnUrl: string;
  message: string;
  hasBeenLoggedIn = false;
  showDropDown = false;
  hidePassword = true;
  @ViewChildren('smsInput') private smsInput: QueryList<ElementRef>;
  activeLang: string = 'sv';
  smsTimer: number = 0;
  smsTimerInterval: any;
  oneMinuteTimer: number = 0;
  oneMinuteTimerInterval: any;

  constructor(
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private stateservice: StateService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    public dialog: MatDialog,
    public snackBar: SnackbarService,
    public authService: AuthService,
    public dialogRef: MatDialogRef<any>,
    public eventQueue: EventQueueService,
    public translate: TranslocoService,
    public myConfigService: MyConfigService,
    @Inject(MAT_DIALOG_DATA) public data: TimedOutData
  ) { }

  ngOnInit() {
    this.hasBeenLoggedIn = this.data.reLogin;
    this.initForm();
    this.activeLang = this.translate.getActiveLang();
    this.translate.langChanges$.subscribe((lang) => {
      this.activeLang = lang;
    });
  }

  ngAfterViewInit() {
    this.smsInput.changes.subscribe((list: QueryList<ElementRef>) => {
      if (list.length > 0) {
        setTimeout(() => {
          list.first.nativeElement.focus();
        });
      }
    });
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.loginForm.controls;
  }

  initForm() {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required]
    });
    this.loginSmsForm = this.fb.group({
      smsCode: this.fb.control(null, [Validators.required, Validators.minLength(6)])
    });
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
  }

  onSubmit() {
    this.loading = true;
    const smsCode = this.loginSmsForm.value.smsCode ? this.loginSmsForm.value.smsCode : '';
    this.stateservice.login(this.f.email.value, this.f.password.value, smsCode).then((loginResult) => {
      if (loginResult.status === 0) {
        this.loading = false;
        const message = this.translate.translate('LOGIN.STATUS.TECHNICAL_PROBLEM');
        this.loginSmsForm.controls['smsCode'].reset();
        this.snackBar.openSnackBar(message, 'OK');
      }
      else if (loginResult.status === 11) {
        this.loading = false;
        this.startSmsTimer();
        if (!this.showSmsCode) {
          this.showSmsCode = true;
        }
        const message = this.translate.translate('LOGIN.STATUS.ENTER_SMS_CODE');
        this.snackBar.openSnackBar(message, 'OK');
      }
      else if (loginResult.status === 12) {
        this.loading = false;
        this.showSmsCode = true;
        this.loginSmsForm.controls['smsCode'].reset();
        const message = this.translate.translate('LOGIN.STATUS.INCORRECT_SMS_CODE');
        this.snackBar.openSnackBar(message, 'OK');
      }
      else if (loginResult.status === 18) {
        this.loading = false;
        this.showSmsCode = true;
        this.clearSmsTimer();
        const message = this.translate.translate('LOGIN.STATUS.SMS_CODE_EXPIRED');
        this.snackBar.openSnackBar(message, 'OK');
      }
      else if (loginResult.status === 7) {
        this.loading = false;
        this.loginSmsForm.controls['smsCode'].reset();
        const message = this.translate.translate('LOGIN.STATUS.LOGIN_FAILED');
        this.snackBar.openSnackBar(message, 'OK');
      } else if (this.data.reLogin === false) {
        const message = (this.translate.translate('LOGIN.STATUS.RELOGIN_SUCCESS') + this.authService.getUserEmail());
        this.snackBar.openSnackBar(message, 'OK');
        this.dialogRef.close();
        this.router.navigate([this.data.currentRoute]);
      } else {
        this.dialog.closeAll();
        this.loading = false;
        const message = (this.translate.translate('LOGIN.STATUS.LOGIN_SUCCESS') + this.authService.getUserEmail());
        this.snackBar.openSnackBar(message, 'OK');

        if (
          !this.router.url.includes('/parkifly-new') &&
          !this.router.url.includes('/parkifly') &&
          !this.router.url.match("flygets/flygets-start") &&
          !this.router.url.match("/flygets-start") &&
          !this.router.url.match("/nazar") &&
          !this.router.url.match("/tui")
        ) {
          if (this.data?.currentRoute) {
            this.router.navigate([this.data.currentRoute]);
          } else if (this.router.url.includes('customer-portal')) {
            this.router.navigate(['parkifly/my-orders/flygets']);
          } else if (this.myConfigService.getMyConfig().showAdmin) {
            this.router.navigate(['admin/admin-start']);
          } else if (this.myConfigService.hasBackOfficeAccess()) {
            this.router.navigate(['admin/back-office24']);
          } else {
            this.router.navigate(['/']);
          }

          // if (this.myConfigService?.getMyConfig()?.showAdmin == true) {
          //   this.router.navigate(['/admin/admin-start']);
          // }
        }
        else {
          this.router.navigate(['/']);
        }
        // }
      }
    });
  }

  getNewPassword() {
    this.dialog.open(ForgotPasswordComponent, {
      width: '420px',
      data: { email: this.loginForm.get('email')?.value || '' }
    });
  }

  startSmsTimer() {
    this.smsTimer = 600; // 10 minutes
    this.oneMinuteTimer = 60; // 1 minute

    this.smsTimerInterval = setInterval(() => {
      this.smsTimer--;
      if (this.smsTimer <= 0) {
        this.clearSmsTimer();
      }
    }, 1000);

    this.oneMinuteTimerInterval = setInterval(() => {
      this.oneMinuteTimer--;
      if (this.oneMinuteTimer <= 0) {
        this.clearOneMinuteTimer();
      }
    }, 1000);
  }

  clearSmsTimer() {
    if (this.smsTimerInterval) {
      clearInterval(this.smsTimerInterval);
      this.smsTimerInterval = null;
      const message = (this.translate.translate('LOGIN.STATUS.SMS_CODE_EXPIRED'));
      this.snackBar.openSnackBar(message, 'OK');
    }
    this.smsTimer = 0;
    this.clearOneMinuteTimer();
  }

  startOneMinuteTimer() {
    this.oneMinuteTimer = 60; // 1 minute
    this.oneMinuteTimerInterval = setInterval(() => {
      this.oneMinuteTimer--;
      if (this.oneMinuteTimer <= 0) {
        this.clearOneMinuteTimer();
      }
    }, 1000);
  }

  clearOneMinuteTimer() {
    if (this.oneMinuteTimerInterval) {
      clearInterval(this.oneMinuteTimerInterval);
      this.oneMinuteTimerInterval = null;
    }
    this.oneMinuteTimer = 0;
  }

  formatOneMinuteTimer(): string {
    const minutes = Math.floor(this.oneMinuteTimer / 60);
    const seconds = this.oneMinuteTimer % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  formatTimer(): string {
    const minutes = Math.floor(this.smsTimer / 60);
    const seconds = this.smsTimer % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  resendSms() {
    this.clearSmsTimer();
    this.clearOneMinuteTimer();
    this.loginSmsForm.controls['smsCode'].reset();
    this.onSubmit();
  }

  goToStart() {
    this.clearSmsTimer();
    this.router.navigate(['/']);
    this.dialog.closeAll();
  }
}
